import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, TrendingUp, Shield, Briefcase, BookOpen } from 'lucide-react';
import usePageMeta from '../../hooks/usePageMeta';

const features = [
  {
    icon: Users,
    title: 'Expert Consultants',
    description: 'Our team brings experience in long-term care operations, strategic planning, and organizational excellence.',
    // description: 'Our team brings experience in healthcare, public health, and strategic planning.',
  },
  {
    icon: TrendingUp,
    title: 'Data-Informed Support',
    description: 'We use analytics and evidence-informed approaches to support practical decision-making.',
  },
  {
    icon: Shield,
    title: 'Trusted Partner',
    description: 'We work alongside your team as a true partner, committed to your long-term success.',
  },
];

const services = [
  'Strategic Consulting & Growth Planning',
  'Professional Training & Development',
  'Staffing & Talent Solutions',
];

export default function HomePage() {
  usePageMeta({
    title: 'Stratel | Healthcare Strategy & Operations Consulting',
    description: 'Transform your healthcare organization with Stratel – experts in strategy, operations, compliance, and digital transformation.',
    keywords: 'long-term care consulting, healthcare consulting, strategy, operations consulting, compliance, digital transformation',
  });
  return (
    <div>
      {/* Hero section */}
      <section className="relative bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight">
              Transform Your Long-Term Care Organization with{' '}
              <span className="text-primary-400">Expert Solutions</span>
            </h1>
            <p className="mt-6 text-xl text-secondary-300 leading-relaxed">
              We deliver strategic consulting, professional training, and staffing solutions 
              to help organizations achieve operational excellence and sustainable growth.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/get-started" className="btn-primary btn-lg">
                Get Started
                <ArrowRight size={20} />
              </Link>
              <Link to="/services" className="btn bg-white/10 text-white hover:bg-white/20 btn-lg">
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services overview */}
      <section className="py-20 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-secondary-900">
                Three Core Service Areas
              </h2>
              <p className="mt-4 text-lg text-secondary-600">
                We provide integrated solutions across consulting, training, and staffing 
                to address your organization's most critical needs.
              </p>
              <ul className="mt-8 space-y-4">
                {services.map((service) => (
                  <li key={service} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <span className="text-secondary-700">{service}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/services/consulting" className="btn-primary inline-flex justify-center sm:justify-start">
                  Consulting Services
                  <ArrowRight size={20} />
                </Link>
                <Link to="/services/training" className="btn bg-blue-600 hover:bg-blue-700 text-white inline-flex justify-center sm:justify-start">
                  Training Services
                  <ArrowRight size={20} />
                </Link>
                <Link to="/services/staffing" className="btn bg-emerald-600 hover:bg-emerald-700 text-white inline-flex justify-center sm:justify-start">
                  Staffing Services
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden min-h-[420px] sm:min-h-[460px] md:h-[520px] bg-gradient-to-br from-primary-50 via-primary-100 to-secondary-50 flex items-center justify-center lg:justify-end p-4 sm:p-6 md:p-12">
                <div className="max-w-sm w-full mx-auto lg:mx-0">
                  <div className="space-y-4 md:space-y-6">
                    {/* connecting flow line */}
                    <svg className="absolute right-36 top-24 hidden md:block" width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M6 114 C40 90 80 30 114 6" stroke="#e6f8f4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
                    </svg>

                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md">
                      <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white flex-shrink-0" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="4" fill="white" /><circle cx="12" cy="12" r="8" stroke="white" strokeWidth="2" fill="none" /></svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-900">Strategic Roadmap</h4>
                        <p className="text-xs text-secondary-500">Clear, actionable plans</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md">
                      <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center text-white flex-shrink-0" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 12h6v6H3zM15 6h6v12h-6z" fill="white"/></svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-900">Measure Performance</h4>
                        <p className="text-xs text-secondary-500">Track KPIs & trends</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md">
                      <div className="w-12 h-12 rounded-full bg-primary-700 flex items-center justify-center text-white flex-shrink-0" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 20h16v-2H4v2zM6 8h2v9H6V8zm4-3h2v12h-2V5zm4 6h2v6h-2v-6z" fill="white"/></svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-900">Actionable Insights</h4>
                        <p className="text-xs text-secondary-500">Data-driven recommendations</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md">
                      <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white flex-shrink-0" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7l3-7z" fill="white"/></svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-900">Drive Growth</h4>
                        <p className="text-xs text-secondary-500">Support measurable progress</p>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve section */}
      <section className="py-20 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-secondary-900">
              Who We Serve
            </h2>
            <p className="mt-4 text-lg text-secondary-600">
              We specialize in supporting long-term care organizations facing unique operational and strategic challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary-600">
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Senior Living Communities</h3>
                  <p className="mt-1 text-secondary-600">Skilled nursing facilities, assisted living, and memory care communities seeking operational excellence.</p>
                </div>
              </div>

              {/*<div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary-600">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Healthcare Organizations</h3>
                  <p className="mt-1 text-secondary-600">Health systems and hospitals expanding into post-acute care and long-term services.</p>
                </div>
              </div>*/}

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary-600">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Growing LTC Organizations</h3>
                  <p className="mt-1 text-secondary-600">Organizations pursuing strategic growth, acquisitions, and new market expansion.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary-600">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Compliance-Focused Leaders</h3>
                  <p className="mt-1 text-secondary-600">Organizations committed to quality standards, staff retention, and regulatory excellence.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">Common Challenges We Address</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Workforce retention and burnout in clinical and support teams</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Financial optimization and operational efficiency</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Strategic positioning and market growth opportunities</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Leadership development and succession planning</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Funding opportunities and grant management</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-secondary-700">Team training and capability building</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-secondary-900">
              Why Partner With Us
            </h2>
            <p className="mt-4 text-lg text-secondary-600">
              We bring the expertise, tools, and commitment needed to transform your organization.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="text-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto">
                    <Icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-secondary-900">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-secondary-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-white">
            Ready to Transform Your Organization?
          </h2>
          <p className="mt-4 text-xl text-primary-100">
            Let's discuss how we can help you achieve your goals.
          </p>
          <Link to="/get-started" className="btn bg-white text-primary-600 hover:bg-primary-50 btn-lg mt-8 inline-flex">
            Schedule a Consultation
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
